'use client';

import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import { initCompass } from '@nerdwallet/compass/next-init-compass';
import { useAnalyticsPageView } from '@repo/platform-analytics/AnalyticsPageView/Context';
import { useAnalytics } from '@repo/platform-analytics/hooks/useAnalytics';
import { env } from '@repo/platform-env';
import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode, useEffect } from 'react';
let compassInitialized = false;
class CompassErrorBoundary extends Component<PropsWithChildren, {
  hasError: boolean;
}> {
  constructor(props: PropsWithChildren) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  override componentDidCatch(error: Error, _info: ErrorInfo) {
    datadogRum.addError(error, {
      message: 'Error initializing Compass'
    });
  }
  override render() {
    if (this.state.hasError) return <div />;
    return this.props.children;
  }
}
function CompassInit() {
  const $ = _c(16);
  const {
    pageViewId,
    pageName,
    contentId,
    contentSource
  } = useAnalyticsPageView();
  const {
    deployableName,
    deployableVersion
  } = useAnalytics();
  let t0;
  if ($[0] !== contentId || $[1] !== contentSource || $[2] !== deployableName || $[3] !== deployableVersion || $[4] !== pageName || $[5] !== pageViewId) {
    t0 = {
      pageViewId,
      deployableName,
      deployableVersion,
      writeKey: env.NEXT_PUBLIC_CLIENT_SEGMENT_WRITE_KEY,
      driverContentId: contentId,
      driverContentSource: contentSource,
      driverPageName: pageName,
      pageName
    };
    $[0] = contentId;
    $[1] = contentSource;
    $[2] = deployableName;
    $[3] = deployableVersion;
    $[4] = pageName;
    $[5] = pageViewId;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const initCompassProps = t0;
  let t1;
  if ($[7] !== initCompassProps) {
    t1 = () => {
      if (compassInitialized) {
        return;
      }
      compassInitialized = true;
      initCompass(initCompassProps);
    };
    $[7] = initCompassProps;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== contentId || $[10] !== contentSource || $[11] !== deployableName || $[12] !== deployableVersion || $[13] !== pageName || $[14] !== pageViewId) {
    t2 = [pageViewId, pageName, contentId, contentSource, deployableName, deployableVersion];
    $[9] = contentId;
    $[10] = contentSource;
    $[11] = deployableName;
    $[12] = deployableVersion;
    $[13] = pageName;
    $[14] = pageViewId;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  useEffect(t1, t2);
  return null;
}
export function CompassPageViewRegistration(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CompassErrorBoundary><CompassInit /></CompassErrorBoundary>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <>{children}{t1}</>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}